import { useCallback, useEffect, useMemo, useState } from 'react';
import { PREFERENCE_KEYS } from 'types/preferenceTypes';
import { debounceAsync } from './debounceAsync';
import { useUserTenantPreferences } from './useUserTenantPreferences';

type UseUserTenantPreferenceOptions = {
  key?: PREFERENCE_KEYS;
  /** Wait time in milliseconds for debounced calls to the save mutation.
   *
   * **Be careful changing this value, as it can cause performance degradation.**
   *
   *  default is 1000.  */
  debounceMs?: number;
};

const EMPTY_PREFERENCE = {};
/** a User Tenant Preference is a user preference that is scoped to the tenant */
export const useUserTenantPreference = <TValue>({
  key,
  debounceMs = 1000,
}: UseUserTenantPreferenceOptions) => {
  const {
    data,
    loading,
    setPreference: _set,
    deletePreference: _delete,
  } = useUserTenantPreferences<TValue>();
  const savedPreferenceValue = data?.find(({ key: k }) => k === key)?.value ?? EMPTY_PREFERENCE;
  const [optimisticValue, setOptimisticValue] = useState<(TValue & { updatedAt: number }) | null>(
    null
  );

  const debouncedSet = useMemo(() => debounceAsync(_set, debounceMs), [_set, debounceMs]);

  useEffect(() => {
    if (optimisticValue && savedPreferenceValue?.updatedAt > optimisticValue?.updatedAt) {
      setOptimisticValue(null);
    }
  }, [optimisticValue, savedPreferenceValue?.updatedAt]);

  const setPreference = useCallback(
    (value: TValue) => {
      const preferenceValue = { ...value, updatedAt: new Date().getTime() };
      setOptimisticValue(preferenceValue);
      return debouncedSet(key, preferenceValue);
    },
    [debouncedSet, key]
  );

  const deletePreference = useCallback(() => {
    if (!key) {
      return Promise.reject('no preference key provided');
    }
    return _delete(key);
  }, [_delete, key]);

  const clearPreference = useCallback(() => {
    return setPreference({} as TValue);
  }, [setPreference]);

  return {
    value: optimisticValue ?? (savedPreferenceValue as TValue),
    loading,
    setPreference,
    deletePreference,
    clearPreference,
  };
};
